import React, { useMemo, useEffect, useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { Layout } from "../../../../components/Layout";
import { calculatorFormControls } from "../../constants/forms";
import { useActions } from "../../../../hooks/useActions";
import {
    loadUbigeos,
    calculateHomePrice,
    resetCalculator,
} from "../../../../redux/home-insurance/home-insurance.actions";
import TypeaheadSelects from "../../../../components/TypeaheadSelects";
import {
    isInvalid,
    getReactHookFormHelpers,
    inputMaxLengthHelpers,
} from "../../../../libs/helpers/forms";
import { HOUSING_TYPE_OPTIONS } from "../../../../constants/housing";
import { HousingInfo } from "../../../../models/housing-info";
import { formatNumber, isZero } from "../../../../libs/helpers/number";
import { calculatorSchema } from "../../constants/schemas";
import SpacingBox from "../../../../components/SpacingBox";
import { Wave } from "../../../../components/Wave";
import useQuotePopup from "../../../../hooks/useQuotePopup";
import useLocationOptions from "./useLocationOptions";
import PriceCalculator from "./PriceCalculator";
import useWindowResize from "../../../../hooks/useWIndowResize";
import { getMonthlyPrice } from "../../../../libs/helpers/price";

import "./styles.css";
import Datalayer from "../../../../libs/vendors/data-layer";
import { CLICK_ACTIONS, CLICK_DESCRIPTION } from "../../../../constants/datalayer";
import Button from "../../../../components/Button/Button";
import TextField from "../../../../components/textfield/textfield";
import Dropdown from "../../../../components/Dropdown/Dropdown";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup"
import Selectable from "../../../../components/Selectable/Selectable";

const CalculatorSection = () => {
    const { isMobile } = useWindowResize();
    const {
        submitting,
        calculateTimes,
        calculatedPrice,
        location,
        housingType,
        squareMeters,
    } = useSelector(state => ({
        ubigeos: state.HomeInsurance.ubigeos,
        ...state.HomeInsurance.landing.calculator,
    }));

    const { locationOptions } = useLocationOptions();
    const { openQuotePopup } = useQuotePopup();
    const rhForm = useForm({
        resolver: yupResolver(calculatorSchema),
        mode: "all",
        defaultValues: {
            location: [],
        },
    });
    const { register, handleSubmit, watch, setValue } = rhForm;


    React.useEffect(() => {
        register(calculatorFormControls.location.key)
        register(calculatorFormControls.district.key)
        register(calculatorFormControls.housingType.key)
    }, [])

    const locationCurrent = watch(calculatorFormControls.location.key);
    const housingTypeCurrent = watch(calculatorFormControls.housingType.key);
    const squareMetersCurrent = watch(calculatorFormControls.squareMeters.key);

    const [showButton, setShowButton] = useState(false);

    const actions = useActions(
        {
            loadUbigeos,
            calculateHomePrice,
            resetCalculator,
        },
        []
    );

    useEffect(() => {
        let st = false;
        if (locationCurrent[0] !== undefined && locationCurrent[0].value !== undefined) {
            st =
                locationCurrent[0].value === location &&
                housingTypeCurrent === housingType &&
                squareMetersCurrent === squareMeters;
        }
        setShowButton(st);
    }, [
        calculateTimes,
        locationCurrent,
        housingTypeCurrent,
        squareMetersCurrent,
        housingType,
        location,
        squareMeters,
        setShowButton,
    ]);

    const getAssetsCalculatorButtton = useMemo(() => {
        let titleButton = "";
        let colorButton = "";
        if (calculateTimes === 0) {
            colorButton = calculatorFormControls.colors.init;
            titleButton = calculatorFormControls.titles.init;
        } else if (showButton) {
            colorButton = calculatorFormControls.colors.start;
            titleButton = calculatorFormControls.titles.start;
        } else {
            colorButton = calculatorFormControls.colors.restart;
            titleButton = calculatorFormControls.titles.restart;
        }
        return { titleButton, colorButton };
    }, [calculateTimes, showButton]);

    const onSubmit = useCallback(
        values => {
            if (calculateTimes === 0 || !showButton) {
                actions.calculateHomePrice(
                    new HousingInfo({
                        ...values,
                        location: values.location,
                    })
                );
                const district = values.district;
                Datalayer.sendClickAction(
                    "Inicio",
                    `${CLICK_ACTIONS.SELECT_BUTTON}: ${CLICK_DESCRIPTION.BTN_CALCULATE}`
                );
                Datalayer.sendClickAction(
                    "Inicio",
                    `${CLICK_ACTIONS.CALCULATE_QUOTATION}: ${district}`
                );
            } else {
                openQuotePopup();
            }
        },
        [actions, calculateTimes, showButton, openQuotePopup]
    );

    useEffect(() => {
        actions.loadUbigeos();
        actions.resetCalculator();
    }, [actions]);

    const number = formatNumber(getMonthlyPrice(calculatedPrice).toFixed(2));

    const price = isZero(number) ? "00.00" : number;
    const sector = isMobile ? "Lima y Callao." : "Lima Metropolitana y Callao.";

    return (
        <>
            <Layout className="landing-calculator" direction="column" justify="center">
                <div className="landing-calculator__content">
                    <div className="landing-calculator__title text-center">
                        <p>
                            Calcula la cuota mensual <br />
                            de tu seguro para tu casa
                        </p>
                    </div>
                    <div className="landing-calculator__price">
                        <PriceCalculator price={price} />
                        <div className="text-thin line-normal landing-calculator__price-valid">
                            <p>
                                Solo es válido para <b>{sector}</b>
                            </p>
                        </div>
                    </div>
                    <FormProvider {...rhForm}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <SpacingBox mb="15">
                                <Selectable
                                    id={calculatorFormControls.location.key}
                                    placeholder={calculatorFormControls.location.placeholder}
                                    name={calculatorFormControls.location.key}
                                    options={locationOptions}
                                    getSelected={(selected) => {
                                        setValue(calculatorFormControls.district.key, selected.district)
                                    }}
                                />

                            </SpacingBox>
                            <SpacingBox mb="15">
                                <Dropdown
                                    fluid
                                    placeholder={
                                        calculatorFormControls.housingType.placeholder
                                    }
                                    id={calculatorFormControls.housingType.key}
                                    name={calculatorFormControls.housingType.key}
                                    options={HOUSING_TYPE_OPTIONS}
                                />
                            </SpacingBox>

                            <TextField
                                fluid
                                darkMode
                                type="number"
                                step="any"
                                inputMode="decimal"
                                placeholder={calculatorFormControls.squareMeters.placeholder}
                                id={calculatorFormControls.squareMeters.key}
                                name={calculatorFormControls.squareMeters.key}
                                {...inputMaxLengthHelpers(
                                    rhForm,
                                    calculatorFormControls.squareMeters.key,
                                    {
                                        lengthValidator: strValue => {
                                            return /^(.{0}|\d{1,10}|\d{1,10}\.\d{0,2})$/.test(
                                                strValue
                                            );
                                        },
                                    }
                                )}
                            />

                            <SpacingBox mt="25">
                                <h4 className="text-thin text-center">
                                    {calculateTimes !== 0 ? (
                                        !submitting ? (
                                            "!Tu inmueble es muy valioso!"
                                        ) : (
                                            <p> &nbsp;</p>
                                        )
                                    ) : (
                                        ""
                                    )}
                                </h4>
                            </SpacingBox>
                            <SpacingBox mt="25">
                                <Button
                                    fluid
                                    id="calculate-price"
                                    className="rounded"
                                    type="submit"
                                    disabled={submitting || !rhForm.formState.isValid}
                                    kind={getAssetsCalculatorButtton.colorButton}
                                >
                                    {getAssetsCalculatorButtton.titleButton}
                                </Button>
                            </SpacingBox>
                        </form>
                    </FormProvider>
                </div>
                <div className="landing-calculator__value-detail">
                    El valor calculado es una estimación basada solo en la cobertura
                    principal del seguro y el costo referencial del m2 de tu casa, no
                    incluye el valor para asegurar tus cosas.
                </div>
                <div className="landing-calculator__bottom-wave">
                    <Wave reverse flip />
                </div>
            </Layout>
        </>
    );
};

export default React.memo(CalculatorSection);
